import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Typography,
  Paper,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  Box,
} from "@material-ui/core";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canUserAccessEmails } from "../../utils/userPermissions";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    overflow: "auto",
    borderRadius: 12,
    ...theme.scrollbarStyles,
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 2),
    gap: theme.spacing(1),
  },
}));

const FOLDER_MAP = {
  "/emails/inbox": "inbox",
  "/emails/sent": "sent",
  "/emails/trash": "trash",
};

const FOLDER_TITLE_KEYS = {
  "/emails/inbox": "emails.subMenus.inbox",
  "/emails/sent": "emails.subMenus.sent",
  "/emails/trash": "emails.subMenus.trash",
};

const Emails = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const folder = FOLDER_MAP[location.pathname] || "inbox";
  const titleKey = FOLDER_TITLE_KEYS[location.pathname] || "mainDrawer.listItems.emails";

  const loadTickets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/tickets", { params: { folder } });
      setTickets(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [location.pathname]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post("/email/sync-all");
      await loadTickets();
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const getPreview = (ticket) => {
    const contact = ticket.contact?.name || ticket.contact?.email || "—";
    const queue = ticket.queue?.name;
    return queue ? `${contact} · ${queue}` : contact;
  };

  if (!canUserAccessEmails(user)) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t(titleKey)}</Title>
      </MainHeader>
      <Paper className={classes.paper} variant="outlined">
        <Box className={classes.toolbar}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <CircularProgress size={18} />
            ) : (
              i18n.t("emails.sync")
            )}
          </Button>
        </Box>
        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} />
          </Box>
        ) : tickets.length === 0 ? (
          <Box className={classes.empty}>
            <EmailOutlinedIcon style={{ fontSize: 48, opacity: 0.35 }} />
            <Typography variant="h6" gutterBottom>
              {i18n.t(titleKey)}
            </Typography>
            <Typography variant="body2">{i18n.t("emails.empty")}</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {tickets.map((ticket) => (
              <ListItem
                key={ticket.id}
                className={classes.listItem}
                onClick={() => history.push(`/tickets/${ticket.uuid}`)}
              >
                <ListItemAvatar>
                  <Avatar>
                    <EmailOutlinedIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={getPreview(ticket)}
                  secondary={new Date(ticket.updatedAt).toLocaleString()}
                />
                <Chip
                  size="small"
                  label={ticket.status}
                  color={ticket.status === "pending" ? "secondary" : "default"}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Emails;
